<script setup>
import {computed, onUnmounted, ref, watch} from 'vue';
import {preparePickedFile, IMAGE_TYPES} from '../fileUploadValidation';
import FilePickerControls from './FilePickerControls.vue';

const props = defineProps({
  disabled: {type: Boolean, default: false},
  idPrefix: {type: String, default: 'license'}
});

const model = defineModel({
  type: Object,
  default: () => ({})
});

// Derived from the model rather than held locally, so a parent that resets the model — cancelling an
// edit, or reloading after a save — clears the tiles too.
const licenseFrontName = computed(() => model.value.driverLicenseFront?.name || '');
const licenseBackName = computed(() => model.value.driverLicenseBack?.name || '');

const frontInputId = computed(() => `${props.idPrefix}-front-input`);
const backInputId = computed(() => `${props.idPrefix}-back-input`);

// The stored-document flag is remembered separately because picking a side clears it on the model:
// removing that side again must not leave a driver whose licence is on file looking like they have none.
const storedDocumentOnFile = ref(!!model.value.hasLicenseDocument);
watch(() => model.value.hasLicenseDocument, (onFile) => {
  if (onFile) storedDocumentOnFile.value = true;
});

const hasExistingLicense = computed(() => model.value.hasLicenseDocument &&
    !model.value.driverLicenseFront && !model.value.driverLicenseBack);

const licenseSidesUploaded = computed(() => {
  if (hasExistingLicense.value) return 2;
  return (model.value.driverLicenseFront ? 1 : 0) + (model.value.driverLicenseBack ? 1 : 0);
});

const preparing = ref({licenseFront: false, licenseBack: false});

// A thumbnail of the side just picked, so a driver on a phone can see they photographed the right
// face of the card. It also replaces the file name as the confirmation that something was picked:
// a camera name is a 30-character digit string with nothing to wrap on, which overflowed the tile.
const previews = ref({licenseFront: '', licenseBack: ''});

function setPreview(side, file) {
  const previous = previews.value[side];
  if (previous) URL.revokeObjectURL(previous);
  previews.value[side] = file && file.type?.startsWith('image/') ? URL.createObjectURL(file) : '';
}

watch(() => model.value.driverLicenseFront, (file) => setPreview('licenseFront', file), {immediate: true});
watch(() => model.value.driverLicenseBack, (file) => setPreview('licenseBack', file), {immediate: true});

onUnmounted(() => {
  setPreview('licenseFront', null);
  setPreview('licenseBack', null);
});

// Bumped when the picker is enabled or disabled, so an in-flight pick can tell that the edit it
// belonged to is over. Keyed on `disabled` alone and not on the model: both parents mutate the
// model object in place rather than replacing it, so a getter watcher on it — which compares by
// identity — would never fire and would only look like protection. The one parent with a cancel
// button (DriverVehicleLicenseManagement) drives `disabled` off the same `isEditing` flag its
// cancel clears, which is exactly the transition that matters. The signup wizard has no cancel,
// passes no `disabled`, and needs no guard.
const editSession = ref(0);
watch(() => props.disabled, () => {
  editSession.value += 1;
});

/**
 * Accepts a picked side, downscaling the photo before it is held for upload.
 *
 * @param {string} side either 'licenseFront' or 'licenseBack'
 * @param {Event} e the change event from the camera or file input
 */
async function onSelect(side, e) {
  const files = e?.target?.files;
  if (!files || files.length === 0) return;

  const input = e.target;
  // Compression is asynchronous and the parent can cancel an edit while it runs, which resets the
  // model. Without this the resolved photo would be written back afterwards, showing a side picked
  // against a driver whose stored licence is untouched and leaving the form permanently dirty.
  const pickedDuring = editSession.value;
  preparing.value[side] = true;
  try {
    const file = await preparePickedFile(files[0], e, IMAGE_TYPES, {subject: "driver's license"});
    if (!file || pickedDuring !== editSession.value) return;

    if (side === 'licenseFront') {
      model.value.driverLicenseFront = file;
    } else {
      model.value.driverLicenseBack = file;
    }
    model.value.hasLicenseDocument = false;
  } finally {
    preparing.value[side] = false;
    // Cleared unconditionally so re-picking the same photo still raises a change event.
    input.value = '';
  }
}

function removeLicenseFront() {
  model.value.driverLicenseFront = null;
  restoreStoredFlagWhenNothingPicked();
}

function removeLicenseBack() {
  model.value.driverLicenseBack = null;
  restoreStoredFlagWhenNothingPicked();
}

function restoreStoredFlagWhenNothingPicked() {
  if (!model.value.driverLicenseFront && !model.value.driverLicenseBack) {
    model.value.hasLicenseDocument = storedDocumentOnFile.value;
  }
}
</script>

<template>
  <div class="driver-license-sides-upload">
    <div v-if="hasExistingLicense" class="alert alert-info py-2 px-3 small mb-3">
      <i class="fa fa-file-alt me-1"></i> License document already on file. Upload new photos below to replace it.
    </div>

    <div class="d-flex flex-wrap align-items-center gap-2 mb-2">
      <span class="badge"
            :class="licenseSidesUploaded === 2 ? 'bg-success' : 'bg-secondary'">
        <i class="fa" :class="licenseSidesUploaded === 2 ? 'fa-check' : 'fa-camera'"></i>
        {{ licenseSidesUploaded }} of 2 sides uploaded
      </span>
      <small class="text-muted">Both front and back are required</small>
    </div>

    <div class="row g-2 mb-3">
      <div class="col-6">
        <div class="border rounded p-2 text-center h-100 d-flex flex-column"
             :class="licenseFrontName ? 'border-success' : ''">
          <div class="license-side-media mb-2">
            <img v-if="previews.licenseFront" class="license-thumb" :src="previews.licenseFront"
                 :alt="'Front of license: ' + licenseFrontName">
            <i v-else class="fa-solid fa-id-card fa-2x"
               :class="licenseFrontName ? 'text-success' : 'text-muted'"></i>
          </div>
          <div class="small f-w-600">Front</div>
          <div v-if="licenseFrontName" class="license-side-status">
            <small class="text-success text-truncate d-block" :title="licenseFrontName">
              <i class="fa fa-check-circle me-1"></i>Added
            </small>
            <button type="button" class="btn btn-link btn-sm text-danger p-0" :disabled="disabled"
                    @click="removeLicenseFront">
              <i class="fa-solid fa-xmark me-1"></i>Remove
            </button>
          </div>
          <FilePickerControls class="mt-auto pt-2" :input-id="frontInputId" accept="image/*"
                              :disabled="disabled || preparing.licenseFront"
                              :camera-label="licenseFrontName ? 'Retake' : 'Take photo'"
                              :file-label="licenseFrontName ? 'Replace' : 'Choose file'"
                              :sole-label="licenseFrontName ? 'Replace' : 'Upload'"
                              @picked="onSelect('licenseFront', $event)"/>
          <small v-if="preparing.licenseFront" class="text-muted d-block mt-1">
            <span class="spinner-border spinner-border-sm me-1"></span>Preparing photo...
          </small>
        </div>
      </div>
      <div class="col-6">
        <div class="border rounded p-2 text-center h-100 d-flex flex-column"
             :class="licenseBackName ? 'border-success' : ''">
          <div class="license-side-media mb-2">
            <img v-if="previews.licenseBack" class="license-thumb" :src="previews.licenseBack"
                 :alt="'Back of license: ' + licenseBackName">
            <i v-else class="fa-solid fa-id-card-clip fa-2x"
               :class="licenseBackName ? 'text-success' : 'text-muted'"></i>
          </div>
          <div class="small f-w-600">Back</div>
          <div v-if="licenseBackName" class="license-side-status">
            <small class="text-success text-truncate d-block" :title="licenseBackName">
              <i class="fa fa-check-circle me-1"></i>Added
            </small>
            <button type="button" class="btn btn-link btn-sm text-danger p-0" :disabled="disabled"
                    @click="removeLicenseBack">
              <i class="fa-solid fa-xmark me-1"></i>Remove
            </button>
          </div>
          <FilePickerControls class="mt-auto pt-2" :input-id="backInputId" accept="image/*"
                              :disabled="disabled || preparing.licenseBack"
                              :camera-label="licenseBackName ? 'Retake' : 'Take photo'"
                              :file-label="licenseBackName ? 'Replace' : 'Choose file'"
                              :sole-label="licenseBackName ? 'Replace' : 'Upload'"
                              @picked="onSelect('licenseBack', $event)"/>
          <small v-if="preparing.licenseBack" class="text-muted d-block mt-1">
            <span class="spinner-border spinner-border-sm me-1"></span>Preparing photo...
          </small>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* A fixed media box keeps the two tiles the same height whether a side shows its thumbnail or the
   placeholder icon, so the Upload controls line up across the pair. */
.license-side-media {
  height: 64px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.license-thumb {
  max-height: 64px;
  max-width: 100%;
  object-fit: contain;
  border-radius: 4px;
}

.license-side-status {
  min-width: 0;
}

.license-side-status .btn-link {
  min-height: 2rem;
}
</style>
